import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { scriptsApi } from '../services/api'
import { useNotificationStore } from './notifications'

export const useScriptStore = defineStore('scripts', () => {
  // State
  const scripts = ref([])
  const currentScript = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const filters = ref({
    client_id: null,
    status: null
  })

  // Getters
  const runningScripts = computed(() =>
    scripts.value.filter(script => script.status === 'running').length
  )
  const stoppedScripts = computed(() =>
    scripts.value.filter(script => script.status === 'stopped').length
  )
  const failedScripts = computed(() =>
    scripts.value.filter(script => script.status === 'failed').length
  )
  const totalScripts = computed(() => scripts.value.length)

  // Actions
  const notificationStore = useNotificationStore()

  const setLoading = (isLoading) => {
    loading.value = isLoading
  }

  const setError = (errorMessage) => {
    error.value = errorMessage
    if (errorMessage) {
      notificationStore.addNotification('error', errorMessage)
    }
  }

  const clearError = () => {
    error.value = null
  }

  const fetchScripts = async (params = {}) => {
    setLoading(true)
    clearError()

    try {
      const requestParams = {
        ...filters.value,
        ...params
      }

      // Remove empty filters
      Object.keys(requestParams).forEach(key => {
        if (requestParams[key] === '' || requestParams[key] === null) {
          delete requestParams[key]
        }
      })

      const scriptList = await scriptsApi.getScripts(requestParams)
      scripts.value = scriptList

      return scriptList
    } catch (error) {
      setError('Error al cargar los scripts: ' + error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const fetchScript = async (id) => {
    setLoading(true)
    clearError()

    try {
      const script = await scriptsApi.getScript(id)
      currentScript.value = script
      return script
    } catch (error) {
      setError('Error al cargar el script: ' + error.message)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const createScript = async (scriptData) => {
    setLoading(true)
    clearError()

    try {
      const newScript = await scriptsApi.createScript(scriptData)

      // Add to local state
      scripts.value.unshift(newScript)

      notificationStore.addNotification('success', 'Script creado y ejecutado exitosamente')
      return newScript
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setError('Error al crear el script: ' + errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateScript = async (id, scriptData) => {
    setLoading(true)
    clearError()

    try {
      const updatedScript = await scriptsApi.updateScript(id, scriptData)

      // Update in local state
      const index = scripts.value.findIndex(script => script.id === id)
      if (index !== -1) {
        scripts.value[index] = updatedScript
      }

      // Update current script if it's the same
      if (currentScript.value && currentScript.value.id === id) {
        currentScript.value = updatedScript
      }

      notificationStore.addNotification('success', 'Script actualizado exitosamente')
      return updatedScript
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setError('Error al actualizar el script: ' + errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const stopScript = async (id, force = false) => {
    setLoading(true)
    clearError()

    try {
      const stoppedScript = await scriptsApi.stopScript(id, force)

      // Update in local state
      const index = scripts.value.findIndex(script => script.id === id)
      if (index !== -1) {
        scripts.value[index] = stoppedScript
      }

      // Update current script if it's the same
      if (currentScript.value && currentScript.value.id === id) {
        currentScript.value = stoppedScript
      }

      notificationStore.addNotification('success', 'Script detenido exitosamente')
      return stoppedScript
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setError('Error al detener el script: ' + errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const restartScript = async (id) => {
    setLoading(true)
    clearError()

    try {
      const restartedScript = await scriptsApi.restartScript(id)

      // Update in local state
      const index = scripts.value.findIndex(script => script.id === id)
      if (index !== -1) {
        scripts.value[index] = restartedScript
      }

      // Update current script if it's the same
      if (currentScript.value && currentScript.value.id === id) {
        currentScript.value = restartedScript
      }

      notificationStore.addNotification('success', 'Script reiniciado exitosamente')
      return restartedScript
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setError('Error al reiniciar el script: ' + errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteScript = async (id) => {
    setLoading(true)
    clearError()

    try {
      await scriptsApi.deleteScript(id)

      // Remove from local state
      scripts.value = scripts.value.filter(script => script.id !== id)

      notificationStore.addNotification('success', 'Script eliminado exitosamente')

      // Clear current script if it's the deleted one
      if (currentScript.value && currentScript.value.id === id) {
        currentScript.value = null
      }

      return true
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setError('Error al eliminar el script: ' + errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getLogs = async (id, logType = 'stdout', lines = 50) => {
    try {
      const logs = await scriptsApi.getLogs(id, logType, lines)
      return logs
    } catch (error) {
      setError('Error al cargar los logs: ' + error.message)
      throw error
    }
  }

  const getProcessStatus = async (id) => {
    try {
      const status = await scriptsApi.getProcessStatus(id)
      return status
    } catch (error) {
      setError('Error al obtener el estado del proceso: ' + error.message)
      throw error
    }
  }

  const parseLogs = async (id, logType = 'stdout') => {
    try {
      const result = await scriptsApi.parseLogs(id, logType)
      notificationStore.addNotification('success', `Log procesado: ${result.devices_found} dispositivos encontrados`)
      return result
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message
      setError('Error al procesar el log: ' + errorMessage)
      throw error
    }
  }

  const getDeviceReports = async (id) => {
    try {
      const reports = await scriptsApi.getDeviceReports(id)
      return reports
    } catch (error) {
      setError('Error al obtener los reportes de dispositivos: ' + error.message)
      throw error
    }
  }

  const setFilters = (newFilters) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const clearFilters = () => {
    filters.value = {
      client_id: null,
      status: null
    }
  }

  const refreshScripts = async () => {
    await fetchScripts()
  }

  // Reset store
  const $reset = () => {
    scripts.value = []
    currentScript.value = null
    loading.value = false
    error.value = null
    filters.value = {
      client_id: null,
      status: null
    }
  }

  return {
    // State
    scripts,
    currentScript,
    loading,
    error,
    filters,

    // Getters
    runningScripts,
    stoppedScripts,
    failedScripts,
    totalScripts,

    // Actions
    fetchScripts,
    fetchScript,
    createScript,
    updateScript,
    stopScript,
    restartScript,
    deleteScript,
    getLogs,
    getProcessStatus,
    parseLogs,
    getDeviceReports,
    setFilters,
    clearFilters,
    refreshScripts,
    clearError,
    $reset
  }
})
